import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { botPhaseService } from "../services/botPhaseService";
import { botPhaseDisplayId } from "./BotPhaseCommonStore";
import { BotPhase } from "../../../api/graphql";
import { getBotStepValues } from "../../botStep/store/BotStepCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { InitializeParams } from "../../../store/types";

export class BotPhaseViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = botPhaseService;
  botPhaseInstance: BotPhase | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  phaseNumberValue: number = 0;
  phaseIdValue: string = "";
  phaseNameValue: string = "";
  statusValue: string = "";
  stepsTotalValue: number = 0;
  stepsCompletedValue: number = 0;
  startedAtValue: Date | null = null;
  completedAtValue: Date | null = null;
  durationValue: number | undefined = undefined;
  errorValue: string = "{}";
  stepsLogPathValue: string = "";
  screenshotFiles: CrudRows = {};
  stepsColumnWidths: Record<string, number | string> = {};
  stepsRows: CrudRows = {};
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.stepsColumnWidths = {
      stepNumber: 200,
      stepId: 200,
      status: 200,
      duration: 200,
    };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadBotPhaseById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.botPhaseOne({ id });
    this.botPhaseInstance = result.botPhaseOne;
    if (this.botPhaseInstance) {
      this.loadValues(this.botPhaseInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.botPhaseInstance) {
      return botPhaseDisplayId(this.botPhaseInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.BotPhaseSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get stepsVisibleColumns() {
    return [
      {
        name: "stepNumber",
        title: this.langStore.BotStepStepNumberLabel,
        width: this.stepsColumnWidths["stepNumber"],
      },
      {
        name: "stepId",
        title: this.langStore.BotStepStepIdLabel,
        width: this.stepsColumnWidths["stepId"],
      },
      {
        name: "status",
        title: this.langStore.BotStepStatusLabel,
        width: this.stepsColumnWidths["status"],
      },
      {
        name: "duration",
        title: this.langStore.BotStepDurationLabel,
        width: this.stepsColumnWidths["duration"],
      },
    ];
  }

  stepsSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.stepsColumnWidths = values;
  }

  get stepsRowsDyn() {
    return this.stepsRows;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "BotPhase",
      fieldName: fieldName,
      instanceId: this.botPhaseInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "BotPhase",
        fieldName: fieldName,
        instanceId: this.botPhaseInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.botPhaseInstance = null;
    this.phaseNumberValue = 0;
    this.phaseIdValue = "";
    this.phaseNameValue = "";
    this.statusValue = "";
    this.stepsTotalValue = 0;
    this.stepsCompletedValue = 0;
    this.startedAtValue = null;
    this.completedAtValue = null;
    this.durationValue = undefined;
    this.errorValue = "{}";
    this.stepsLogPathValue = "";
    this.screenshotFiles = {};
    this.stepsRows = {};
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelBotPhase() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editBotPhase() {
    const id = this.botPhaseInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/botPhase/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printBotPhase(handlePrint: () => void): void {
    handlePrint();
  }

  *closeBotPhase(): any {
    yield this.close();
  }

  loadValues(instance: BotPhase) {
    this.idValue = instance.id;
    this.phaseNumberValue = instance.phaseNumber;
    this.phaseIdValue = instance.phaseId;
    this.phaseNameValue = instance.phaseName;
    this.statusValue = instance.status;
    this.stepsTotalValue = instance.stepsTotal;
    this.stepsCompletedValue = instance.stepsCompleted;
    this.startedAtValue = instance.startedAt
      ? new Date(instance.startedAt)
      : null;
    this.completedAtValue = instance.completedAt
      ? new Date(instance.completedAt)
      : null;
    this.durationValue = instance.duration || undefined;
    this.errorValue = instance.error || "";
    this.stepsLogPathValue = instance.stepsLogPath || "";
    this.screenshotFiles = instance.screenshot_data
      ? {
          [instance.screenshot_data.name]: {
            name: instance.screenshot_data.name,
            formattedSize: formatBytes(instance.screenshot_data?.size || 0),
            mimetype: instance.screenshot_data?.mimetype,
            id: instance.screenshot_data.name,
            __status__: "original",
          },
        }
      : {};
    this.stepsRows = instance.steps
      ? Object.assign(
          {},
          ...instance.steps.map((x) => {
            const processedRow = getBotStepValues(x, this.langStore, false);
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadBotPhaseById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
